import { useMemo } from 'react';
import useBrand from 'Hooks/useBrand';
import useUser from 'Hooks/useUser';
import { UserPermissionOnBrand } from 'modules/Const';

/** 
 * Enum of features that can be restricted for View Only users
 */
export enum ViewOnlyFeature {
  // Maestro Features
  MAESTRO_ACCESS = 'maestro_access',
  
  // Website Features
  WEBSITE_GALLERY = 'website_gallery',
  WEBSITE_EDIT_SITE = 'website_edit_site',
  WEBSITE_PIXELS_EDIT = 'website_pixels_edit',
  
  // Website Builder Features
  WEBSITE_BUILDER_SAVE = 'website_builder_save',
  WEBSITE_BUILDER_EDIT_URL = 'website_builder_edit_url',
  WEBSITE_BUILDER_DELETE = 'website_builder_delete',
  WEBSITE_BUILDER_BUILD_TAB = 'website_builder_build_tab',
  WEBSITE_BUILDER_STYLE_TAB = 'website_builder_style_tab',
  WEBSITE_BUILDER_CONNECT_TAB = 'website_builder_connect_tab',
  
  // Settings Features
  SETTINGS_DELETE_ACCOUNT = 'settings_delete_account',
  SETTINGS_BILLING = 'settings_billing',
  SETTINGS_INVOICES = 'settings_invoices',
  
  // Team Features
  TEAM_VIEW_MEMBERS = 'team_view_members',
  TEAM_INVITE_MEMBER = 'team_invite_member',
  
  // Campaign Features
  CAMPAIGN_CREATE = 'campaign_create',
  CAMPAIGN_DUPLICATE = 'campaign_duplicate',
  CAMPAIGN_RENAME = 'campaign_rename',
  CAMPAIGN_ARCHIVE = 'campaign_archive',
  CAMPAIGN_EDIT_BUDGET = 'campaign_edit_budget',
  CAMPAIGN_EDIT_END_DATE = 'campaign_edit_end_date',
  CAMPAIGN_EXTEND = 'campaign_extend',
  CAMPAIGN_PAUSE_RESUME = 'campaign_pause_resume',
  CAMPAIGN_PAUSE_ASSET = 'campaign_pause_asset',
  CAMPAIGN_UPLOAD_ASSET = 'campaign_upload_asset',
}

/**
 * Feature definitions with user-friendly names and descriptions
 */
const VIEW_ONLY_FEATURES = {
  [ViewOnlyFeature.MAESTRO_ACCESS]: {
    name: 'Maestro AI Assistant',
    description: 'Access to AI-powered music marketing assistant'
  },
  [ViewOnlyFeature.WEBSITE_GALLERY]: {
    name: 'Website Gallery',
    description: 'Browse and use website templates from gallery'
  },
  [ViewOnlyFeature.WEBSITE_EDIT_SITE]: {
    name: 'Edit Website',
    description: 'Edit and customize website content'
  },
  [ViewOnlyFeature.WEBSITE_PIXELS_EDIT]: {
    name: 'Edit Website Pixels',
    description: 'Configure website tracking pixels'
  },
  [ViewOnlyFeature.WEBSITE_BUILDER_SAVE]: {
    name: 'Save Website Changes',
    description: 'Save changes in website builder'
  },
  [ViewOnlyFeature.WEBSITE_BUILDER_EDIT_URL]: {
    name: 'Edit Website URL',
    description: 'Modify website URL and slug'
  },
  [ViewOnlyFeature.WEBSITE_BUILDER_DELETE]: {
    name: 'Delete Website',
    description: 'Delete website and its content'
  },
  [ViewOnlyFeature.WEBSITE_BUILDER_BUILD_TAB]: {
    name: 'Website Build Tab',
    description: 'Access website building and editing tools'
  },
  [ViewOnlyFeature.WEBSITE_BUILDER_STYLE_TAB]: {
    name: 'Website Style Tab',
    description: 'Customize website appearance and styling'
  },
  [ViewOnlyFeature.WEBSITE_BUILDER_CONNECT_TAB]: {
    name: 'Website Connect Tab',
    description: 'Connect website to external services and platforms'
  },
  [ViewOnlyFeature.SETTINGS_DELETE_ACCOUNT]: {
    name: 'Delete Account',
    description: 'Delete user account and all associated data'
  },
  [ViewOnlyFeature.SETTINGS_BILLING]: {
    name: 'Billing Settings',
    description: 'View and manage billing information'
  },
  [ViewOnlyFeature.SETTINGS_INVOICES]: {
    name: 'View Invoices',
    description: 'Access billing invoices and payment history'
  },
  [ViewOnlyFeature.TEAM_VIEW_MEMBERS]: {
    name: 'View Team Members',
    description: 'View team members and their permissions'
  },
  [ViewOnlyFeature.TEAM_INVITE_MEMBER]: {
    name: 'Invite Team Member',
    description: 'Invite new members to join the team'
  },
  [ViewOnlyFeature.CAMPAIGN_CREATE]: {
    name: 'Create Campaign',
    description: 'Create new marketing campaigns'
  },
  [ViewOnlyFeature.CAMPAIGN_DUPLICATE]: {
    name: 'Duplicate Campaign',
    description: 'Create a copy of an existing campaign'
  },
  [ViewOnlyFeature.CAMPAIGN_RENAME]: {
    name: 'Rename Campaign',
    description: 'Change campaign name and title'
  },
  [ViewOnlyFeature.CAMPAIGN_ARCHIVE]: {
    name: 'Archive Campaign',
    description: 'Archive or unarchive campaigns'
  },
  [ViewOnlyFeature.CAMPAIGN_EDIT_BUDGET]: {
    name: 'Edit Campaign Budget',
    description: 'Modify campaign spending budget'
  },
  [ViewOnlyFeature.CAMPAIGN_EDIT_END_DATE]: {
    name: 'Edit Campaign End Date',
    description: 'Change when the campaign ends'
  },
  [ViewOnlyFeature.CAMPAIGN_EXTEND]: {
    name: 'Extend Campaign',
    description: 'Extend finished campaigns with new budget and dates'
  },
  [ViewOnlyFeature.CAMPAIGN_PAUSE_RESUME]: {
    name: 'Pause/Resume Campaign',
    description: 'Pause or resume campaign execution'
  },
  [ViewOnlyFeature.CAMPAIGN_PAUSE_ASSET]: {
    name: 'Pause Campaign Asset',
    description: 'Pause individual assets within a campaign'
  },
  [ViewOnlyFeature.CAMPAIGN_UPLOAD_ASSET]: {
    name: 'Upload Campaign Asset',
    description: 'Upload new assets to existing campaigns'
  },
} as const;

export interface UseViewOnlyAccessResult {
  /** Whether the user has access to this feature (not restricted by View Only) */
  hasAccess: boolean;
  /** Whether the user is currently in View Only mode */
  isViewOnlyUser: boolean;
  /** Get a restriction message for this feature */
  getRestrictionMessage: () => string;
}

/**
 * Hook to check if a feature is accessible for View Only users
 * This hook specifically handles permission-level restrictions, not plan-based restrictions
 * 
 * @param feature - The feature to check access for
 * @returns Object with access information and restriction messages
 */
export const useViewOnlyAccess = (feature: ViewOnlyFeature): UseViewOnlyAccessResult => {
  const { currentBrand } = useBrand();
  const { isSymphonyAdmin } = useUser();

  const isViewOnlyUser = currentBrand?.permissionLevel === UserPermissionOnBrand.VIEW_ONLY;
  
  const hasAccess = useMemo(() => {
    // Symphony admins bypass all restrictions
    if (isSymphonyAdmin) {
      return true;
    }
    
    // Non-View Only users have access to everything
    if (!isViewOnlyUser) {
      return true;
    }
    
    // View Only users are restricted from all features in this enum
    return false;
  }, [isViewOnlyUser, isSymphonyAdmin]);

  const getRestrictionMessage = () => {
    if (!hasAccess && isViewOnlyUser) {
      const featureInfo = VIEW_ONLY_FEATURES[feature];
      return `Members with "View Only" access cannot access ${featureInfo.name}`;
    }
    return '';
  };

  return {
    hasAccess,
    isViewOnlyUser,
    getRestrictionMessage,
  };
};

/**
 * Convenience hook to check if the current user is in View Only mode
 * @returns boolean indicating if user has View Only access
 */
export const useIsViewOnlyUser = (): boolean => {
  const { currentBrand } = useBrand();
  return currentBrand?.permissionLevel === UserPermissionOnBrand.VIEW_ONLY;
};
